import type {
  GetTransactionsPaginatedQueryParams,
  GetTransactionsWithReceiptDto,
} from '@pflegenavi/shared/api';
import type { PaginatedResultSet } from '@pflegenavi/shared/utils';
import type {
  GetTransactionsPhoenixParams,
  GetTransactionsPhoenixResult,
  TransactionPhoenixDto,
} from './api';

export function convertNodeJSParamsToPhoenix(
  params: GetTransactionsPaginatedQueryParams & { nursingHomeId: string }
): GetTransactionsPhoenixParams {
  const tiebreakerFields = ['sequence_year', 'sequence_id', 'id'];

  const convertSortBy = (
    sortBy: string | undefined,
    sortOrder: 'asc' | 'desc' = 'desc'
  ): { fields: string[]; directions: Array<'asc' | 'desc'> } | undefined => {
    if (!sortBy) {
      return undefined;
    }

    let primaryField: string | undefined;
    switch (sortBy) {
      case 'submissionDate':
        primaryField = 'inserted_at';
        break;
      case 'receiptDate':
        primaryField = 'receipt_date';
        break;
      case 'amount':
        primaryField = 'amount';
        break;
      case 'additionalFeeAmount':
        primaryField = 'additional_fee_amount';
        break;
      default:
        return undefined;
    }

    const fields = [primaryField, ...tiebreakerFields];
    const directions = fields.map(() => sortOrder);
    return { fields, directions };
  };

  const sortConfig =
    params.sortBy && params.sortOrder
      ? convertSortBy(params.sortBy, params.sortOrder as 'asc' | 'desc')
      : undefined;

  return {
    nursing_home_id: params.nursingHomeId,
    page: params.page + 1, // Convert from 0-indexed to 1-indexed
    page_size: params.pageSize,
    resident_id: params.residentIds?.[0],
    family_member_id: params.familyMemberId,
    type: params.transactionTypes,
    source_type: params.transactionSourceTypes,
    initiated_by: params.initiators,
    // submission date links to the transaction creation date in the legacy version
    creation_date_range: params.submissionDateRange
      ? [params.submissionDateRange.gte, params.submissionDateRange.lte]
      : undefined,
    receipt_date_range: params.receiptDateRange
      ? [params.receiptDateRange.gte, params.receiptDateRange.lte]
      : undefined,
    creator_ids: params.creatorIds,
    service_provider_ids: params.serviceProviderIds,
    receipt_type_ids: params.receiptTypeIds,
    search: params.search?.value,
    order_by: sortConfig?.fields,
    order_directions: sortConfig?.directions,
  };
}

// Convert Phoenix result to Node.js result format
export function convertPhoenixResultToNodeJS(
  result: GetTransactionsPhoenixResult
): PaginatedResultSet<GetTransactionsWithReceiptDto> {
  const data: GetTransactionsWithReceiptDto[] = result.data.map(
    (transaction: TransactionPhoenixDto) => ({
      id: transaction.id,
      type: transaction.type,
      sourceType: transaction.source_type,
      resident: {
        id: transaction.resident.id,
        firstName: transaction.resident.firstname,
        lastName: transaction.resident.lastname,
      },
      familyMemberId: transaction.family_member_id,
      receiptId: transaction.receipt?.id ?? '',
      receiptType: transaction.receipt_type?.name ?? undefined,
      receiptDate: transaction.receipt_date
        ? new Date(transaction.receipt_date)
        : undefined,
      receiptCreatedOn: transaction.receipt?.created_on
        ? new Date(transaction.receipt.created_on)
        : undefined,
      serviceProvider: transaction.service_provider?.name ?? '',
      amount: transaction.amount / 100, // Convert from cents to euros
      amountInCent: transaction.amount,
      additionalFeeAmountInCent: transaction.additional_fee_amount,
      includedFeeAmountInCent: transaction.included_fee_amount,
      additionalFeesDivertedToPflegenavi:
        transaction.additional_fees_diverted_to_pflegenavi,
      // Map created_on (which is inserted_at in Phoenix) to date field for legacy compatibility
      date: new Date(transaction.created_on),
      creatorName: transaction.creator_name,
      creatorId: transaction.creator_id,
      receiptImageIds: transaction.receipt_image_ids || [],
      receipt_not_available:
        transaction.receipt?.receipt_not_available || false,
      notes: transaction.notes || undefined,
      cancelled: transaction.cancelled,
      disputed: transaction.disputed,
      recurringItem: transaction.recurring_item || undefined,
      cancelledSubmissionDate: transaction.cancelled_submission_date
        ? new Date(transaction.cancelled_submission_date)
        : undefined,
      initiatedBy: transaction.initiated_by,
      ibanLast4: undefined, // Not included in Phoenix response
      sequenceID: transaction.sequence_id,
    })
  );

  return {
    data,
    meta: {
      currentPage: result.meta.current_page - 1, // Convert from 1-indexed to 0-indexed
      totalPages: result.meta.total_pages,
      totalItems: result.meta.total_count,
      pageSize: result.meta.page_size,
      itemsPerPage: result.meta.page_size,
      hasNext: result.meta.has_next_page,
      hasPrevious: result.meta.has_previous_page,
    },
    computeAndSetLinks: () => {
      // Not needed for Phoenix implementation
    },
  };
}
